import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Building2, Coins, Users, DollarSign } from "lucide-react";
import { fmt, fmtCompact } from "@/lib/format";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

export default function AdminDashboard() {
  const [a, setA] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics").then(({ data }) => setA(data)).catch(() => {});
  }, []);

  if (!a) return <div className="p-8 text-gray-500">Loading...</div>;

  const stats = [
    { icon: Building2, label: "Properties", value: a.total_properties },
    { icon: Coins, label: "Bricks sold", value: `${a.sold_bricks}/${a.total_bricks}`, accent: true },
    { icon: Users, label: "Investors", value: a.total_investors },
    { icon: DollarSign, label: "Revenue", value: fmt(a.total_revenue), accent: true },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      <div className="overline">Admin</div>
      <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-white mt-2 mb-8">
        Control room
      </h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className={`bp-surface rounded-2xl p-5 ${s.accent ? "border-[#D4AF37]/30" : ""}`} data-testid={`admin-stat-${s.label.toLowerCase()}`}>
            <s.icon className={`w-4 h-4 mb-3 ${s.accent ? "text-[#D4AF37]" : "text-gray-400"}`} />
            <div className="overline text-[10px]">{s.label}</div>
            <div className={`font-heading text-2xl md:text-3xl mt-2 font-light ${s.accent ? "text-[#D4AF37]" : "text-white"}`}>
              {s.value}
            </div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-6">
        <div className="bp-surface rounded-2xl p-6 md:col-span-2">
          <div className="overline mb-4">Revenue · last 14 days</div>
          {a.revenue_by_day.length === 0 ? (
            <div className="text-gray-500 text-sm py-10 text-center">No revenue yet</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={a.revenue_by_day}>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis dataKey="date" stroke="#6B7280" fontSize={11} />
                  <YAxis stroke="#6B7280" fontSize={11} />
                  <Tooltip
                    contentStyle={{
                      background: "#0B1120",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: "8px",
                      color: "#fff",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="amount"
                    stroke="#D4AF37"
                    strokeWidth={2}
                    dot={{ fill: "#D4AF37", r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        <div className="bp-surface rounded-2xl p-6">
          <div className="overline mb-4">Top properties</div>
          <div className="space-y-3">
            {a.top_properties.length === 0 ? (
              <div className="text-gray-500 text-sm">No data</div>
            ) : (
              a.top_properties.map((p, i) => (
                <div key={p.property_id} className="flex items-center justify-between">
                  <div>
                    <div className="text-white text-sm">{p.name}</div>
                    <div className="text-[11px] text-gray-500">{p.bricks} bricks</div>
                  </div>
                  <div className="font-mono text-sm text-[#D4AF37]">{fmtCompact(p.revenue)}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
